import { useEffect, useMemo, useReducer, useRef, useState, CSSProperties } from "react";
import type { Bundle } from "./types";
import { CONDITIONS, type ConditionKey } from "./types";
import { initialState, reducer, type UIState } from "./state";
import { loadBundle, listBundles, DEMO_ID, type BundleListItem } from "./bundle";
import { Telemetry } from "./telemetry";
import { INK, SURFACE } from "./style";
import { wordDiff, splitParas } from "./diff";
import { ReadingPane } from "./panels/ReadingPane";
import { AxisProfile } from "./panels/AxisProfile";
import { ContrastDrawer } from "./panels/ContrastDrawer";
import { RewriteDiff } from "./panels/RewriteDiff";
import { ContextPanel } from "./panels/ContextPanel";
import { Reframe } from "./panels/Reframe";
import { HighlightTooltip } from "./panels/HighlightTooltip";
import { Interstitial, SkippedScreen } from "./panels/Interstitial";
import { ConsentGate, ConsentStopped } from "./panels/ConsentGate";
import { readConsent, writeConsent, clearConsent, type ConsentRecord } from "./consent";
import { TelemetryPanel } from "./panels/TelemetryPanel";
import { SurveyItem } from "./panels/SurveyItem";
import { SourceViewer } from "./panels/SourceViewer";
import { ChangeDetail } from "./panels/ChangeDetail";
import type { SourceLinkMeta } from "./panels/SourceLink";
import { readRoute, replaceRoute, pushRoute, onRouteChange } from "./router";

export function App() {
  const [bundle, setBundle] = useState<Bundle | null>(null);
  const [list, setList] = useState<BundleListItem[]>([]);
  // Deep link: #/<cond>/<id> may name the boot stimulus; absent/invalid → demo default.
  const [curId, setCurId] = useState<number>(() => readRoute().id ?? DEMO_ID);
  const bootFromHash = useRef(readRoute().id != null);
  const [err, setErr] = useState<string | null>(null);

  // ---- consent (spec §7): first-time entry is gated; a stored agreement skips it ----
  const [consent, setConsent] = useState<ConsentRecord | null>(() => readConsent());
  const [stopped, setStopped] = useState<"declined" | "withdrawn" | null>(null);
  const [reviewOpen, setReviewOpen] = useState(false);

  // Session-scoped telemetry, owned here (not by <Lens/>) so session_id and the
  // pseudonymous participant code survive stimulus switches — and so the consent
  // decision itself can be logged before any stimulus exists.
  const curIdRef = useRef(curId); curIdRef.current = curId;
  const telRef = useRef<Telemetry | null>(null);
  if (!telRef.current) {
    telRef.current = new Telemetry(() => ({ condition: "pre_study", join_gva_id: curIdRef.current, target_outlet: "" }));
  }
  const T = telRef.current;

  // returning participant: reuse their code and note the restore once, on boot
  useEffect(() => {
    const c = readConsent();
    if (!c) return;
    T.participant_id = c.participant_id;
    T.log("consent_restored", { version: c.version, agreed_at: c.agreed_at });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    listBundles().then(setList).catch(() => setList([]));
  }, []);

  useEffect(() => {
    let alive = true;
    setBundle(null);
    setErr(null);
    loadBundle(curId)
      .then((b) => { if (alive) { bootFromHash.current = false; setBundle(b); } })
      .catch((e) => {
        if (!alive) return;
        // the hash named an unknown incident — fall back to the default stimulus
        if (bootFromHash.current && curId !== DEMO_ID) { bootFromHash.current = false; setCurId(DEMO_ID); }
        else setErr(String(e));
      });
    return () => { alive = false; };
  }, [curId]);

  const onAgree = () => {
    const rec = writeConsent();
    T.participant_id = rec.participant_id;
    T.enabled = true;
    setStopped(null);
    setConsent(rec);
  };
  const onWithdraw = () => {
    clearConsent();
    T.stop(); // sends the withdrawal, then stops recording
    setReviewOpen(false);
    setConsent(null);
    setStopped("withdrawn");
  };

  // Nothing about the stimulus is mounted (and no telemetry beyond the consent
  // decision is collected) until consent exists — the bundle fetch above is the only
  // work that runs behind this gate.
  if (!consent) {
    if (stopped) return <ConsentStopped reason={stopped} onReview={() => setStopped(null)} />;
    return <ConsentGate mode="entry" tel={T} onAgree={onAgree} onDecline={() => setStopped("declined")} />;
  }

  if (err) return <Centered>Could not load stimulus {curId}: {err}</Centered>;
  if (!bundle) return <Centered>Loading…</Centered>;

  return (
    <>
      <Lens
        key={bundle.incident.join_gva_id}
        b={bundle}
        list={list}
        curId={curId}
        onPick={setCurId}
        tel={T}
        onOpenConsent={() => setReviewOpen(true)}
      />
      {reviewOpen && (
        <ConsentGate mode="review" record={consent} tel={T}
          onClose={() => setReviewOpen(false)} onWithdraw={onWithdraw} />
      )}
    </>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", color: "rgba(55,53,47,.6)", fontSize: 15 }}>
      {children}
    </div>
  );
}

function Lens({ b, list, curId, onPick, tel: T, onOpenConsent }: {
  b: Bundle; list: BundleListItem[]; curId: number; onPick: (id: number) => void;
  tel: Telemetry; onOpenConsent: () => void;
}) {
  const [s, set] = useReducer(reducer, b, initialState);
  const [, forceTick] = useState(0);

  // ---- telemetry (session-scoped instance owned by <App/>) ----
  const condKey = CONDITIONS[s.cond].key;
  T.onChange = () => forceTick((t) => t + 1);
  // keep the ctx getter fresh as condition changes
  (T as any).getCtx = () => ({ condition: condKey, join_gva_id: b.incident.join_gva_id, target_outlet: b.target.outlet });

  // ---- lifecycle: dwell + scroll + resize ----
  const viewStart = useRef<number>(Date.now());
  const firstDvTs = useRef<number | null>(null);
  const maxScroll = useRef(0);
  const lastScrollLog = useRef(0);
  const lastHoverLog = useRef(0);

  useEffect(() => {
    viewStart.current = Date.now();
    T.log("incident_view_start");
    const onScroll = () => {
      const h = document.documentElement;
      const denom = (h.scrollHeight - window.innerHeight) || 1;
      const pct = Math.min(100, Math.round((window.scrollY / denom) * 100));
      if (pct > maxScroll.current) maxScroll.current = pct;
      const now = Date.now();
      if (now - lastScrollLog.current > 700) { lastScrollLog.current = now; T.log("scroll_depth", { max_pct: maxScroll.current }); }
    };
    const onResize = () => { const n = window.innerWidth < 1080; if (n !== s.narrow) set({ narrow: n }); };
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResize);
    onResize();
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
      T.log("incident_view_end", { dwell_ms: Date.now() - viewStart.current });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- flash-scroll (P2 tap → P1 span) ----
  useEffect(() => {
    if (!s.flashAxis) return;
    const raf = requestAnimationFrame(() => {
      const el = document.querySelector('[data-flash="1"]');
      if (el) { const r = el.getBoundingClientRect(); const y = window.scrollY + r.top - 170; window.scrollTo({ top: Math.max(0, y), behavior: "smooth" }); }
    });
    const t = setTimeout(() => set({ flashAxis: null }), 2600);
    return () => { cancelAnimationFrame(raf); clearTimeout(t); };
  }, [s.flashAxis]);

  // ---- derived: layout ----
  const has = (p: string) => CONDITIONS[s.cond].panels.includes(p as any);
  const showP7 = has("P7");
  const useStacked = s.layout === "stacked" || s.narrow;
  const compact = s.layout === "compact" && !s.narrow;
  // The right rail hosts P4 (hidden in current configs) or, in C5, the on-request reframer (P7).
  const hasLeft = has("P2"), hasRail = has("P4") || showP7;

  const containerStyle: CSSProperties = useStacked
    ? {
        maxWidth: "760px", margin: "22px auto 60px", padding: "0 18px", display: "grid",
        gridTemplateColumns: "minmax(0,1fr)",
        gridTemplateAreas: [hasLeft ? '"left"' : "", '"main"', hasRail ? '"rail"' : ""].filter(Boolean).join(" "),
        gap: "18px",
      }
    : (() => {
        const cols: string[] = [], areas: string[] = [];
        if (hasLeft) { cols.push(compact ? "232px" : "258px"); areas.push("left"); }
        cols.push(compact ? "minmax(0,640px)" : "minmax(0,720px)"); areas.push("main");
        if (hasRail) { cols.push(showP7 ? (compact ? "352px" : "392px") : (compact ? "284px" : "320px")); areas.push("rail"); }
        return {
          maxWidth: compact ? "1180px" : "1360px", margin: "22px auto 60px", padding: "0 22px",
          display: "grid", gridTemplateColumns: cols.join(" "), gridTemplateAreas: '"' + areas.join(" ") + '"',
          gap: "24px", justifyContent: "center", alignItems: "start",
        } as CSSProperties;
      })();
  const asideStyle: CSSProperties = { gridArea: "left", position: useStacked ? "static" : "sticky", top: "104px", alignSelf: "start" };
  const railStyle: CSSProperties = {
    gridArea: "rail", position: useStacked ? "static" : "sticky", top: "104px", alignSelf: "start",
    background: useStacked ? SURFACE.warmer : "transparent", border: useStacked ? "1px solid rgba(55,53,47,.1)" : "none",
    borderRadius: "6px", padding: useStacked ? "18px" : "0",
    ...(showP7 && !useStacked ? { maxHeight: "calc(100vh - 124px)", overflowY: "auto" as const } : {}),
  };
  const mainColStyle: CSSProperties = { gridArea: "main", minWidth: 0 };

  // ---- derived: active reframe (P7) ----
  const RF = b.reframe;
  const active = (s.customActive && s.liveResult)
    ? s.liveResult
    : (s.reframeProfile && RF.precomputed[s.reframeProfile]) || RF.precomputed[RF.default_profile_id];
  const isAsRead = !!active?.as_read;
  const invariantOk = active?.invariant ? active.invariant.ok : true;
  const reframed = showP7 && !isAsRead && invariantOk;
  const body = reframed ? active.rewritten_body : b.target.body;
  const headline = reframed ? active.rewritten_headline : b.target.headline;
  // C5: render the applied reframe as tracked changes over the reading pane itself.
  const reframeTrace = (reframed && active)
    ? {
        paras: splitParas((active.diff_ops && active.diff_ops.length) ? active.diff_ops : wordDiff(b.target.body, active.rewritten_body)),
        headlineBefore: b.target.headline,
        headlineAfter: active.rewritten_headline,
      }
    : null;

  // ---- handlers ----
  const setCond = (k: ConditionKey) => {
    set({
      cond: k, readView: "original", activeChange: null, activeClaim: null, activeAxis: null, pinnedHl: null,
      groundedClaim: null, flashAxis: null, sourceView: null, reframeProfile: RF.default_profile_id,
      customActive: false, liveResult: null, liveLoading: false, reframeChangeOpen: null, advancedOpen: false,
    });
  };
  const onSkip = () => { T.log("incident_skip", {}); set({ skipped: true }); };

  // ---- hash routing (deep links: #/<cond>/<id>[/source/<outlet>]) ----
  const pushedSource = useRef(false);
  // keep the hash in sync with condition/stimulus (replaceState — no history spam)
  useEffect(() => {
    replaceRoute(s.cond, curId, s.sourceView);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [s.cond, curId]);
  // back/forward (and hand-edited hashes): apply the route to state
  useEffect(() => onRouteChange((rt) => {
    if (rt.id != null && rt.id !== curId) { onPick(rt.id); return; } // remount re-reads the route
    if (rt.cond && rt.cond !== s.cond) setCond(rt.cond);
    if ((rt.source ?? null) !== s.sourceView) set({ sourceView: rt.source ?? null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [s.cond, s.sourceView, curId]);

  const pickStimulus = (id: number) => {
    if (id === curId) return;
    replaceRoute(s.cond, id, null); // carry the condition; drop any open source view
    onPick(id);
  };
  const openSource = (outlet: string, meta: SourceLinkMeta) => {
    T.log("source_link_open", { outlet, ...meta });
    pushedSource.current = true;
    set({ sourceView: outlet });
    pushRoute(s.cond, curId, outlet); // history entry, so browser Back closes the viewer
  };
  const closeSource = () => {
    if (pushedSource.current) {
      pushedSource.current = false;
      window.history.back(); // hashchange clears sourceView
    } else {
      // deep-linked straight into the viewer — don't back out of the app
      set({ sourceView: null });
      replaceRoute(s.cond, curId, null);
    }
  };

  // C5: click a reading-pane trace span (a reframe edit carrying a change_ref) → the same
  // floating rationale popover the P5 diff uses, anchored to the clicked edit.
  const onTraceOp = (ref: number, rect: DOMRect) => {
    if (s.reframeChangeOpen === ref) { set({ reframeChangeOpen: null, groundedClaim: null }); return; }
    T.log("reframe_trace_change_open", { change_index: ref, axis: active?.change_log?.[ref]?.axis, profile_id: active?.profile_id });
    const left = Math.max(12, Math.min(rect.left, window.innerWidth - 392));
    let top = rect.bottom + 8; if (top > window.innerHeight - 300) top = Math.max(12, rect.top - 300);
    set({ reframeChangeOpen: ref, groundedClaim: null, tipPos: { top, left }, pinnedHl: null, activeChange: null });
  };
  // the open reframe change (guard out-of-range refs → render nothing)
  const reframeChange = (s.reframeChangeOpen != null && reframed && active)
    ? (active.change_log?.[s.reframeChangeOpen] ?? null) : null;

  const dvOptions = ["Very unfairly", "Somewhat unfairly", "Neutral", "Somewhat fairly", "Very fairly"];
  const onDv = (v: number) => {
    if (firstDvTs.current == null) firstDvTs.current = Date.now();
    T.log("dv_submit", { item_id: "fairness", value: v, time_to_first_dv_ms: (firstDvTs.current as number) - viewStart.current });
    set({ dvValue: v });
  };

  const cfg = CONDITIONS[s.cond];

  return (
    <div data-theme="light" style={{ minHeight: "100vh", background: "#fff", color: INK, fontFamily: "'Source Sans 3',sans-serif" }}>
      {!s.acked && !s.skipped && (
        <Interstitial warningList={b.content_warning.join(", ")} onAck={() => set({ acked: true })} onSkip={onSkip} />
      )}
      {s.skipped && <SkippedScreen onReturn={() => set({ skipped: false, acked: true })} />}

      {/* ===== TOP CHROME ===== */}
      <header style={{ position: "sticky", top: 0, zIndex: 40, background: "rgba(255,255,255,.86)", backdropFilter: "blur(8px)", borderBottom: "1px solid rgba(55,53,47,.1)" }}>
        <div style={{ maxWidth: 1360, margin: "0 auto", padding: "10px 22px", display: "flex", alignItems: "center", gap: 18, flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flex: "none" }}>
            <div style={{ width: 28, height: 28, borderRadius: 6, background: INK, display: "flex", alignItems: "center", justifyContent: "center", flex: "none" }}>
              <div style={{ width: 12, height: 12, border: "2px solid #fff", borderRadius: "50%", position: "relative" }}>
                <div style={{ position: "absolute", width: 5, height: 5, background: "#fff", borderRadius: "50%", top: 1.5, left: 1.5 }} />
              </div>
            </div>
            <div>
              <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 15.5, fontWeight: 700, lineHeight: 1, letterSpacing: "-.01em" }}>Incident Lens</div>
              <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: ".06em", textTransform: "uppercase", color: "rgba(55,53,47,.4)", marginTop: 2 }}>Phase 0 · study renderer</div>
            </div>
          </div>

          {/* condition switcher */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, flex: "none" }}>
            <span style={{ fontSize: 10, fontWeight: 600, letterSpacing: ".06em", textTransform: "uppercase", color: "rgba(55,53,47,.4)" }}>Condition</span>
            <div style={{ display: "flex", background: SURFACE.chip, border: "1px solid rgba(55,53,47,.08)", borderRadius: 7, padding: 2, gap: 2 }}>
              {(Object.keys(CONDITIONS) as ConditionKey[]).map((k) => {
                const on = k === s.cond;
                return (
                  <button key={k} title={CONDITIONS[k].title} onClick={() => setCond(k)}
                    style={{ font: "inherit", fontSize: 12.5, fontWeight: 600, padding: "5px 11px", borderRadius: 5, cursor: "pointer", border: "none", background: on ? INK : "transparent", color: on ? "#fff" : "rgba(55,53,47,.6)", transition: "all .12s" }}>
                    {CONDITIONS[k].label}
                  </button>
                );
              })}
            </div>
          </div>

          <div style={{ flex: 1, minWidth: 8 }} />

          {/* reviewer controls */}
          <div style={{ display: "flex", alignItems: "center", gap: 14, flex: "none", flexWrap: "wrap" }}>
            {list.length > 1 && (
              <LabeledSelect label="Stimulus" value={String(curId)} onChange={(v) => pickStimulus(Number(v))}
                options={list.map((it) => ({ value: String(it.join_gva_id), label: `${it.city_and_state} · ${it.outlet_display}` }))} wide />
            )}
            <LabeledSelect label="Highlights" value={s.highlightStyle} onChange={(v) => set({ highlightStyle: v as any })}
              options={[{ value: "fill", label: "Fill" }, { value: "underline", label: "Underline" }, { value: "bracket", label: "Bracket" }]} />
            <LabeledSelect label="Diff" value={s.diffStyle} onChange={(v) => set({ diffStyle: v as any })}
              options={[{ value: "inline", label: "Inline" }, { value: "sidebyside", label: "Side-by-side" }]} />
            <LabeledSelect label="Layout" value={s.layout} onChange={(v) => set({ layout: v as any })}
              options={[{ value: "margins", label: "Margins" }, { value: "stacked", label: "Stacked" }, { value: "compact", label: "Compact" }]} />
            <button onClick={() => set({ devOpen: !s.devOpen })} title="Toggle telemetry stream"
              style={{ display: "flex", alignItems: "center", gap: 6, background: s.devOpen ? INK : "#fff", color: s.devOpen ? "#fff" : "rgba(55,53,47,.65)", border: "1px solid " + (s.devOpen ? INK : "rgba(55,53,47,.16)"), borderRadius: 6, padding: "6px 11px", fontSize: 12.5, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
              <span style={{ width: 6, height: 6, borderRadius: "50%", background: INK }} />
              Telemetry <span style={{ fontFamily: "'IBM Plex Mono',ui-monospace,monospace", opacity: 0.6 }}>{T.events.length}</span>
            </button>
            <button onClick={onSkip} title="Skip this story (ethics control)"
              style={{ background: "#fff", border: "1px solid rgba(55,53,47,.16)", color: "rgba(55,53,47,.7)", borderRadius: 6, padding: "6px 11px", fontSize: 12.5, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
              Skip story
            </button>
            <button onClick={onOpenConsent} title="Reread what you agreed to, or withdraw"
              style={{ background: "#fff", border: "1px solid rgba(55,53,47,.16)", color: "rgba(55,53,47,.7)", borderRadius: 6, padding: "6px 11px", fontSize: 12.5, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
              Study info
            </button>
          </div>
        </div>

        {/* explainer strip */}
        <div style={{ borderTop: "1px solid rgba(55,53,47,.07)", background: SURFACE.warmer }}>
          <div style={{ maxWidth: 1360, margin: "0 auto", padding: "7px 22px", display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
            <span style={{ fontSize: 12.5, color: "rgba(55,53,47,.6)" }}>{cfg.desc}</span>
            <div style={{ flex: 1, minWidth: 8 }} />
            <div style={{ display: "flex", gap: 5 }}>
              {(["P1", "P2", "P3", "P4", "P5", "P6", "P7"] as const).map((p) => {
                const on = cfg.panels.includes(p);
                const names: Record<string, string> = { P1: "Reading pane", P2: "Framing profile", P3: "Highlights", P4: "Cross-outlet claims", P5: "Neutral rewrite", P6: "Context", P7: "Reframe / what-if" };
                return (
                  <span key={p} title={names[p] + (on ? " · active" : " · hidden")}
                    style={{ fontFamily: "'IBM Plex Mono',ui-monospace,monospace", fontSize: 10, fontWeight: 600, padding: "3px 7px", borderRadius: 4, background: on ? INK : "#efeeea", color: on ? "#fff" : "rgba(55,53,47,.4)", letterSpacing: ".02em" }}>
                    {p}
                  </span>
                );
              })}
            </div>
          </div>
        </div>
      </header>

      {/* ===== MAIN GRID ===== */}
      <main style={containerStyle}>
        {has("P2") && (
          <aside style={asideStyle}>
            <AxisProfile b={b} flashAxis={s.flashAxis}
              onLeanTap={(axis) => { T.log("framing_lean_tap", { axis }); set({ flashAxis: axis, activeAxis: axis }); }} />
          </aside>
        )}

        <section style={mainColStyle}>
          {has("P5") && (
            <DocViewTabs view={s.readView} hasP3={has("P3")}
              onChange={(v) => { if (v !== s.readView) { T.log("doc_view_toggle", { view: v }); set({ readView: v, pinnedHl: null }); } }} />
          )}

          {has("P5") && s.readView === "edit" ? (
            <RewriteDiff b={b} s={s} set={set} tel={T} onOpenSource={openSource} />
          ) : (
          <ReadingPane
            b={b} body={body} headline={headline}
            showP3={has("P3")} highlightsOn={s.highlightsOn} highlightStyle={s.highlightStyle}
            showP7={showP7} reframed={reframed} active={active} invariantOk={invariantOk} trace={reframeTrace}
            flashAxis={s.flashAxis} pinnedHl={s.pinnedHl} activeClaim={s.activeClaim} activeAxis={s.activeAxis}
            onTraceOp={onTraceOp} activeTraceRef={s.reframeChangeOpen}
            onToggleHighlights={() => set({ highlightsOn: !s.highlightsOn, pinnedHl: null })}
            onHlClick={(id, rect) => {
              const hl = b.highlights.find((h) => h.id === id);
              if (!hl) return;
              const left = Math.max(12, Math.min(rect.left, window.innerWidth - 352));
              let top = rect.bottom + 8; if (top > window.innerHeight - 220) top = Math.max(12, rect.top - 212);
              T.log("highlight_tap", { highlight_id: id, ref: hl.ref, kind: hl.kind });
              set({ pinnedHl: id, activeChange: null, reframeChangeOpen: null, groundedClaim: null, tipPos: { top, left }, revealCross: false, activeClaim: hl.cross_outlet?.claim_id ?? s.activeClaim });
            }}
            onHlHover={(id) => {
              const now = Date.now(); if (now - lastHoverLog.current < 400) return; lastHoverLog.current = now;
              const hl = b.highlights.find((h) => h.id === id);
              if (hl) T.log("highlight_hover", { highlight_id: id, ref: hl.ref, kind: hl.kind });
            }}
          />
          )}

          {has("P6") && b.rewrite.optional_context_block ? (
            <ContextPanel open={s.contextOpen} block={b.rewrite.optional_context_block}
              onToggle={() => { const nx = !s.contextOpen; T.log("context_panel_toggle", { expanded: nx }); set({ contextOpen: nx }); }} />
          ) : null}

          <SurveyItem options={dvOptions} value={s.dvValue} onPick={onDv} />
        </section>

        {hasRail && (
          <aside style={railStyle}>
            {has("P4") && <ContrastDrawer b={b} s={s} set={set} tel={T} onOpenSource={openSource} />}
            {showP7 && (
              <Reframe
                b={b} s={s} set={set} tel={T} active={active} reframed={reframed}
                isAsRead={isAsRead} invariantOk={invariantOk} useStacked={true}
              />
            )}
          </aside>
        )}
      </main>

      {/* ===== OVERLAYS ===== */}
      {s.pinnedHl && (
        <HighlightTooltip b={b} s={s} onOpenSource={openSource}
          onClose={() => set({ pinnedHl: null, revealCross: false })}
          onRevealCross={() => {
            const hl = b.highlights.find((h) => h.id === s.pinnedHl);
            if (hl?.cross_outlet) T.log("cross_outlet_open", { highlight_id: s.pinnedHl, alt_outlet: hl.cross_outlet.alt_outlet });
            set({ revealCross: true });
          }} />
      )}

      {/* C5 reframe-trace rationale — same shared popover as the P5 diff */}
      {reframeChange && (
        <ChangeDetail ch={reframeChange} b={b} tipPos={s.tipPos}
          onClose={() => set({ reframeChangeOpen: null, groundedClaim: null })}
          groundedClaim={s.groundedClaim} setGroundedClaim={(id) => set({ groundedClaim: id })}
          tel={T} onOpenSource={openSource} fromChange={s.reframeChangeOpen} />
      )}

      {s.sourceView && (
        <SourceViewer b={b} outlet={s.sourceView} tel={T} onClose={closeSource} />
      )}

      {s.devOpen && (
        <TelemetryPanel events={T.events} onClear={() => T.clear()} onClose={() => set({ devOpen: false })} />
      )}
    </div>
  );
}

// ---- document-view switcher (P1 reading view ⇄ P5 edit trace) ----
function DocViewTabs({ view, hasP3, onChange }: {
  view: "original" | "edit"; hasP3: boolean; onChange: (v: "original" | "edit") => void;
}) {
  const tabs: { id: "original" | "edit"; label: string; glyph: string; hint: string }[] = [
    { id: "original", label: hasP3 ? "Original + highlights" : "Original", glyph: "¶", hint: "The article as published" },
    { id: "edit", label: "Edit trace", glyph: "⇄", hint: "Neutralized rewrite, shown as tracked changes" },
  ];
  const active = tabs.find((t) => t.id === view) || tabs[0];
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14, flexWrap: "wrap" }}>
      <div style={{ display: "flex", background: SURFACE.chip, border: "1px solid rgba(55,53,47,.08)", borderRadius: 7, padding: 2, gap: 2 }}>
        {tabs.map((t) => {
          const on = t.id === view;
          return (
            <button key={t.id} title={t.hint} onClick={() => onChange(t.id)}
              style={{ font: "inherit", fontSize: 12.5, fontWeight: 600, padding: "6px 13px", borderRadius: 5, cursor: "pointer", border: "none", background: on ? INK : "transparent", color: on ? "#fff" : "rgba(55,53,47,.6)", display: "inline-flex", alignItems: "center", gap: 6, transition: "all .12s" }}>
              <span style={{ fontFamily: "'IBM Plex Mono',ui-monospace,monospace", fontSize: 11, opacity: on ? 0.85 : 0.5 }}>{t.glyph}</span>
              {t.label}
            </button>
          );
        })}
      </div>
      <span style={{ fontSize: 11.5, color: "rgba(55,53,47,.45)" }}>{active.hint}</span>
    </div>
  );
}

// ---- small shared chrome control ----
function LabeledSelect({ label, value, onChange, options, wide }: {
  label: string; value: string; onChange: (v: string) => void;
  options: { value: string; label: string }[]; wide?: boolean;
}) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
      <span style={{ fontSize: 9.5, fontWeight: 600, letterSpacing: ".05em", textTransform: "uppercase", color: "rgba(55,53,47,.38)" }}>{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)}
        style={{ fontFamily: "inherit", fontSize: 12.5, color: INK, background: "#fff", border: "1px solid rgba(55,53,47,.16)", borderRadius: 5, padding: "4px 7px", cursor: "pointer", maxWidth: wide ? 240 : undefined }}>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}
